import { useState } from 'react';
import { motion } from 'framer-motion';
import { Send, MapPin, Mail, Phone, CheckCircle } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fadeUp = {
  initial: { opacity: 0, y: 40 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, amount: 0 },
  transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] }
};

const fadeIn = {
  initial: { opacity: 0, y: 30 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] }
};

const INDUSTRIES_OPTIONS = [
  'Government', 'Defense', 'Energy', 'Oil & Gas', 'Railways', 'Satellite', 'Education', 'Other'
];

export default function ContactPage() {
  const [formData, setFormData] = useState({
    name: '', email: '', company: '', industry: '', message: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.message) {
      toast.error('Please fill in all required fields.');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, formData);
      setSubmitted(true);
      toast.success('Message sent successfully!');
    } catch (err) {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page">
      {/* Header */}
      <section className="pt-24 md:pt-32 pb-8 md:pb-16 relative">
        <div className="absolute inset-0 bg-surface grid-bg" />
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8">
          <motion.p {...fadeIn} className="text-xs uppercase tracking-[0.3em] text-brand-accent font-bold mb-4 md:mb-6">
            Get In Touch
          </motion.p>
          <motion.h1
            {...fadeIn}
            transition={{ ...fadeIn.transition, delay: 0.1 }}
            className="text-4xl sm:text-5xl md:text-7xl lg:text-8xl font-heading font-black tracking-tight leading-[0.9] mb-6 md:mb-8"
          >
            Let&apos;s
            <br />
            <span className="text-gradient-cyan">Talk</span>
          </motion.h1>
        </div>
      </section>

      {/* Contact Content */}
      <section className="py-8 md:py-16">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-16">
            {/* Form - shown first on mobile, right on desktop */}
            <motion.div
              {...fadeUp}
              transition={{ ...fadeUp.transition, delay: 0.1 }}
              className="lg:col-span-7 order-1 lg:order-2"
            >
              {submitted ? (
                <div data-testid="contact-success" className="glass-card rounded-lg p-12 text-center">
                  <CheckCircle className="w-16 h-16 text-emerald-500 mx-auto mb-6" />
                  <h3 className="text-2xl font-heading font-bold mb-4">Message Received</h3>
                  <p className="text-slate-400 text-sm">
                    Our team will review your inquiry and get back to you within 24 hours.
                  </p>
                  <button
                    onClick={() => { setSubmitted(false); setFormData({ name: '', email: '', company: '', industry: '', message: '' }); }}
                    data-testid="contact-reset"
                    className="mt-8 text-sm text-slate-400 hover:text-white uppercase tracking-widest underline underline-offset-8 transition-colors duration-200"
                  >
                    Send Another Message
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} data-testid="contact-form" className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-xs uppercase tracking-[0.15em] text-slate-500 font-bold mb-2">
                        Name *
                      </label>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        data-testid="contact-name"
                        className="input-dark w-full h-14 px-4 text-sm"
                        placeholder="Your full name"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-xs uppercase tracking-[0.15em] text-slate-500 font-bold mb-2">
                        Email *
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        data-testid="contact-email"
                        className="input-dark w-full h-14 px-4 text-sm"
                        placeholder="your@email.com"
                        required
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-xs uppercase tracking-[0.15em] text-slate-500 font-bold mb-2">
                        Company
                      </label>
                      <input
                        type="text"
                        name="company"
                        value={formData.company}
                        onChange={handleChange}
                        data-testid="contact-company"
                        className="input-dark w-full h-14 px-4 text-sm"
                        placeholder="Your organization"
                      />
                    </div>
                    <div>
                      <label className="block text-xs uppercase tracking-[0.15em] text-slate-500 font-bold mb-2">
                        Industry
                      </label>
                      <select
                        name="industry"
                        value={formData.industry}
                        onChange={handleChange}
                        data-testid="contact-industry"
                        className="input-dark w-full h-14 px-4 text-sm appearance-none cursor-pointer"
                      >
                        <option value="" className="bg-surface-paper">Select industry</option>
                        {INDUSTRIES_OPTIONS.map((ind) => (
                          <option key={ind} value={ind} className="bg-surface-paper">{ind}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs uppercase tracking-[0.15em] text-slate-500 font-bold mb-2">
                      Message *
                    </label>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      data-testid="contact-message"
                      className="input-dark w-full px-4 py-4 text-sm min-h-[160px] resize-none"
                      placeholder="Tell us about your project or challenge..."
                      required
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={submitting}
                    data-testid="contact-submit"
                    className="inline-flex items-center gap-2 bg-white text-black font-bold text-sm uppercase tracking-wider px-10 py-5 hover:bg-slate-200 transition-colors duration-200 disabled:opacity-50 w-full sm:w-auto justify-center"
                  >
                    {submitting ? 'Sending...' : 'Send Message'} <Send size={16} />
                  </button>
                </form>
              )}
            </motion.div>

            {/* Info - shown second on mobile, left on desktop */}
            <motion.div {...fadeUp} className="lg:col-span-5 order-2 lg:order-1">
              <h2 className="text-2xl font-heading font-bold mb-6">Start a Conversation</h2>
              <p className="text-slate-400 text-sm leading-relaxed mb-12">
                Whether you have a specific AI challenge or want to explore possibilities, we&apos;re here to help. Our team typically responds within 24 hours.
              </p>

              <div className="space-y-8">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 border border-white/10 flex items-center justify-center flex-shrink-0">
                    <MapPin size={16} className="text-brand-accent" />
                  </div>
                  <div>
                    <h4 className="text-sm font-semibold mb-1">Headquarter</h4>
                    <p className="text-slate-500 text-sm">Artificial Mind, Lusail Marine Tower 50</p>
                    <p className="text-slate-500 text-sm">Floor 16, Office 03, Building 5, Zone 69</p>
                    <p className="text-slate-500 text-sm">Street 315, Lusail City, Doha</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 border border-white/10 flex items-center justify-center flex-shrink-0">
                    <Phone size={16} className="text-brand-accent" />
                  </div>
                  <div>
                    <h4 className="text-sm font-semibold mb-1">Phone</h4>
                    <a href="tel:+97440408953" className="text-slate-500 text-sm hover:text-white transition-colors duration-200">+974 4040 8953</a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 border border-white/10 flex items-center justify-center flex-shrink-0">
                    <Mail size={16} className="text-brand-accent" />
                  </div>
                  <div>
                    <h4 className="text-sm font-semibold mb-1">Email</h4>
                    <a href="mailto:anupam@artificialmind.io" className="block text-slate-500 text-sm hover:text-white transition-colors duration-200">anupam@artificialmind.io</a>
                    <a href="mailto:maleida@artificialmind.io" className="block text-slate-500 text-sm hover:text-white transition-colors duration-200">maleida@artificialmind.io</a>
                  </div>
                </div>

              </div>

              <div className="mt-12 glass-card rounded-lg p-6">
                <p className="text-xs uppercase tracking-[0.2em] text-slate-500 font-bold mb-3">Security Clearance</p>
                <p className="text-slate-400 text-sm leading-relaxed">
                  We maintain top-level security clearances for defense and government projects. Classified discussions available upon request.
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
}
